import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { SITE_URL, abs } from "@/content/seo";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { GROUP_HOURS_LABEL } from "@/content/booking";

const title = "Mapa strony | Sylwia Sitkowska";
const description =
  "Mapa strony: wszystkie podstrony serwisu Sylwii Sitkowskiej: wystąpienia, mastermind dla liderów, konsultacje indywidualne, media i publikacje, kontakt i rezerwacja.";

const sections: { heading: string; links: { to: string; label: string; note: string }[] }[] = [
  {
    heading: "Start",
    links: [
      { to: "/", label: "Strona główna", note: "Skrót oferty, ścieżki współpracy i kontakt" },
    ],
  },
  {
    heading: "Praca z liderami",
    links: [
      {
        to: "/mastermind",
        label: "Mastermind dla liderów",
        note: `Kameralna grupa liderów, 9 spotkań, ${GROUP_HOURS_LABEL}, 18 000 zł brutto`,
      },
      {
        to: "/konsultacje",
        label: "Konsultacje indywidualne",
        note: "Indywidualne spotkania dla liderów, 90 minut, 1 000 zł brutto",
      },
      {
        to: "/psychologia-biznesu",
        label: "Psychologia biznesu",
        note: "Artykuły o odpowiedzialności, decyzjach, relacjach, delegowaniu, presji i wypaleniu",
      },
      {
        to: "/rezerwacja",
        label: "Rezerwacja spotkania",
        note: "Formularz rezerwacji konsultacji indywidualnej",
      },

    ],
  },
  {
    heading: "Psychologia biznesu",
    links: [
      {
        to: "/syndrom-oszusta",
        label: "Syndrom oszusta",
        note: "Czym jest fenomen oszusta i dlaczego sukces nie zmienia przekonań o sobie",
      },
      {
        to: "/perfekcjonizm",
        label: "Perfekcjonizm",
        note: "Kiedy wysokie standardy przestają pomagać",
      },
      {
        to: "/stres-w-pracy-a-decyzje",
        label: "Stres a podejmowanie decyzji",
        note: "Decydowanie pod presją i sześć pytań przed dużą decyzją",
      },
      {
        to: "/samotnosc-lidera",
        label: "Samotność lidera",
        note: "Dlaczego trudno prosić o pomoc i kiedy samodzielność kosztuje",
      },
      {
        to: "/wypalenie-zawodowe-u-lidera",
        label: "Wypalenie zawodowe u lidera",
        note: "Dziewięć objawów i to, co pomaga, zanim koszt stanie się wysoki",
      },
      {
        to: "/mastermind-dla-przedsiebiorcow",
        label: "Mastermind dla przedsiębiorców",
        note: "Czym jest mastermind, jak działa i kiedy naprawdę pomaga",
      },
      {
        to: "/delegowanie-zadan",
        label: "Delegowanie zadań",
        note: "Dlaczego tak trudno oddać kontrolę i jak delegować odpowiedzialność",
      },
      {
        to: "/psychologia-przywodztwa",
        label: "Psychologia przywództwa",
        note: "Co dzieje się z liderem pod presją",
      },
    ],
  },
  {
    heading: "O mnie i wystąpienia",
    links: [
      { to: "/o-mnie", label: "O mnie", note: "Doświadczenie, kwalifikacje zawodowe, droga" },
      {
        to: "/wystapienia",
        label: "Wystąpienia i współpraca",
        note: "Keynote'y, panele, spotkania zamknięte, najbliższe wydarzenia",
      },
      {
        to: "/media-i-publikacje",
        label: "Media i publikacje",
        note: "Wypowiedzi w mediach, podcasty, książki",
      },
    ],
  },
  {
    heading: "Kontakt i formalności",
    links: [
      { to: "/kontakt", label: "Kontakt", note: "Formularz kontaktowy i dane" },
      {
        to: "/polityka-prywatnosci",
        label: "Polityka prywatności",
        note: "Zasady przetwarzania danych osobowych",
      },
    ],
  },
];

export const Route = createFileRoute("/sitemap")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/sitemap` },
      { property: "og:image", content: abs(ogSocial) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: abs(ogSocial) },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/sitemap` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Strona główna", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Mapa strony", item: `${SITE_URL}/sitemap` },
          ],
        }),
      },
    ],
  }),
  component: SitemapPage,
});

function SitemapPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <p className="eyebrow">Mapa strony</p>
            <h1 className="display-xl mt-8 max-w-3xl text-[2.4rem] md:text-[3.4rem]">
              Wszystkie strony w jednym miejscu
            </h1>
            <p className="measure mt-8 text-base leading-relaxed text-ink/80 md:text-lg">
              Pełna lista podstron serwisu. Jeśli szukasz konkretnej informacji o pracy
              wystąpieniach, mastermindzie dla liderów albo pracy indywidualnej, zacznij stąd.
            </p>
          </div>
        </section>

        <section className="bg-surface">
          <div className="container-page section-pad">
            <div className="grid gap-px border border-steel bg-steel md:grid-cols-2">
              {sections.map((section) => (
                <div key={section.heading} className="bg-surface p-6 md:p-8">
                  <h2 className="eyebrow">{section.heading}</h2>
                  <ul className="mt-6 space-y-5">
                    {section.links.map((link) => (
                      <li key={link.to}>
                        <Link
                          to={link.to}
                          className="text-[1.1rem] leading-snug underline underline-offset-4 transition-colors hover:text-coral-text"
                        >
                          {link.label}
                        </Link>
                        <p className="mt-1 text-base leading-relaxed text-ink-soft">{link.note}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-10 text-base text-ink-soft">
              Wersja dla wyszukiwarek:{" "}
              <a
                href="/sitemap.xml"
                className="underline underline-offset-4 hover:text-coral-text"
                aria-label="Mapa strony w formacie XML"
              >
                sitemap.xml
              </a>
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
